import { RoomType } from "building-types/rooms/roomTypesEnum";
import type {
  AreaRule,
  AreaRuleByRoomType,
  FloorAreaTakeoffConfig,
  FloorAreaTakeoffConfigs,
} from "building-types/rooms/takeoffTypes";
import {
  AreaRuleType,
  BorderPosition,
  FloorAreaTakeoffType,
  FloorFilter,
  SupplementalArea,
} from "building-types/rooms/takeoffTypes";
import type { PlanSettings } from "building-types/settings/planSettings";
import { ConditionedAreaCalculationType } from "building-types/settings/settingsTypesEnum";
import { defaultAreaReportSettings } from "doc/settings/defaultSettings";
import { areaRuleNew } from "doc/utils/floorAreaTakeoffTools";
import {
  FloorAreaTakeoffConfigsV2,
  TotalFloorAreaAndConditionedStairsFirstFloorOnlyV2,
  TotalFloorAreaAndConditionedV2,
} from "./takeoffConfigV2";

export function isAreaRuleByRoomType(rule: AreaRule): rule is AreaRuleByRoomType {
  return rule.type === AreaRuleType.BY_ROOM_TYPE;
}

export function makeFloorAreaTakeOffConfigs({
  areaReportSettings = defaultAreaReportSettings,
}: Pick<PlanSettings, "areaReportSettings">): FloorAreaTakeoffConfigs {
  const conditionedAreaCalculationType =
    areaReportSettings[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA]?.areaCalculationType ??
    ConditionedAreaCalculationType.BY_ROOM_TYPE;

  const conditionedAreaSettings = areaReportSettings[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA];
  const stairAreaFirstFloorOnlyOnTotalFloorAndConditionedArea = !!conditionedAreaSettings?.stairAreaFirstFloorOnly;

  let unsanitized: { [key in FloorAreaTakeoffType]?: FloorAreaTakeoffConfig };

  if (conditionedAreaCalculationType === ConditionedAreaCalculationType.BY_ROOM_TYPE) {
    unsanitized = Object.assign({}, FloorAreaTakeoffConfigurations);

    if (stairAreaFirstFloorOnlyOnTotalFloorAndConditionedArea) {
      unsanitized[FloorAreaTakeoffType.TOTAL_FLOOR_AREA] = TotalFloorAreaAndConditionedStairsFirstFloorOnly;
      unsanitized[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA] = TotalFloorAreaAndConditionedStairsFirstFloorOnly;
    } else {
      unsanitized[FloorAreaTakeoffType.TOTAL_FLOOR_AREA] = TotalFloorAreaAndConditioned;
      unsanitized[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA] = TotalFloorAreaAndConditioned;
    }
  } else {
    unsanitized = Object.assign({}, FloorAreaTakeoffConfigsV2);

    if (stairAreaFirstFloorOnlyOnTotalFloorAndConditionedArea) {
      unsanitized[FloorAreaTakeoffType.TOTAL_FLOOR_AREA] = TotalFloorAreaAndConditionedStairsFirstFloorOnlyV2;
      unsanitized[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA] = TotalFloorAreaAndConditionedStairsFirstFloorOnlyV2;
    } else {
      unsanitized[FloorAreaTakeoffType.TOTAL_FLOOR_AREA] = TotalFloorAreaAndConditionedV2;
      unsanitized[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA] = TotalFloorAreaAndConditionedV2;
    }
  }

  const configs: FloorAreaTakeoffConfigs = {};

  for (const key in unsanitized) {
    const { include, exclude, includeSupplementalAreas } = unsanitized[key] as FloorAreaTakeoffConfig;
    const newConfig: FloorAreaTakeoffConfig = { include: [], exclude: [], includeSupplementalAreas };

    for (const includeInstance of include) {
      newConfig.include.push(areaRuleNew(includeInstance));
    }

    for (const excludeInstance of exclude) {
      newConfig.exclude.push(areaRuleNew(excludeInstance));
    }

    configs[key] = newConfig;
  }

  return configs;
}

/** currently total floor area and conditioned floor area are the same */
const TotalFloorAreaAndConditioned: FloorAreaTakeoffConfig = {
  include: [],
  exclude: [
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.PATIO]: true,
        [RoomType.FRONT_PORCH]: true,
        [RoomType.REAR_PORCH]: true,
        [RoomType.WALK]: true,
        [RoomType.BREEZEWAY]: true,
        [RoomType.FREESTANDING_FIREPLACE]: true,
        [RoomType.OPEN_BELOW]: true,
        [RoomType.PORTICO]: true,
        [RoomType.LANDSCAPE]: true,
      },
      includeAnyInSameRoomSets: false,
      otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
    },
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.GARAGE]: true,
      },
      includeAnyInSameRoomSets: true,
      otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
    },
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.GARAGE_DETACH]: true,
      },
      includeAnyInSameRoomSets: true,
    },
  ],
};

const TotalFloorAreaAndConditionedStairsFirstFloorOnly: FloorAreaTakeoffConfig = {
  include: [],
  exclude: [
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.PATIO]: true,
        [RoomType.FRONT_PORCH]: true,
        [RoomType.REAR_PORCH]: true,
        [RoomType.WALK]: true,
        [RoomType.BREEZEWAY]: true,
        [RoomType.FREESTANDING_FIREPLACE]: true,
        [RoomType.OPEN_BELOW]: true,
        [RoomType.PORTICO]: true,
        [RoomType.LANDSCAPE]: true,
      },
      includeAnyInSameRoomSets: false,
      otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
    },
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.GARAGE]: true,
      },
      includeAnyInSameRoomSets: true,
      otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
    },
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.GARAGE_DETACH]: true,
      },
      includeAnyInSameRoomSets: true,
    },
    {
      type: AreaRuleType.BY_ROOM_TYPE,
      roomTypes: {
        [RoomType.STAIRS]: true,
        [RoomType.STAIRS_EDITOR]: true,
      },
      includeAnyInSameRoomSets: false,
      otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
      appliesOnlyTo: FloorFilter.UPPER_FLOORS_ONLY,
    },
  ],
};

const FloorAreaTakeoffConfigurations: { [key in FloorAreaTakeoffType]?: FloorAreaTakeoffConfig } = {
  [FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.GARAGE]: true,
        },
        includeAnyInSameRoomSets: true,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
        specificRoomBorderPositions: [
          {
            type: AreaRuleType.BY_ROOM_TYPE,
            roomTypes: {
              [RoomType.PATIO]: true,
              [RoomType.FRONT_PORCH]: true,
              [RoomType.REAR_PORCH]: true,
              [RoomType.WALK]: true,
              [RoomType.BREEZEWAY]: true,
              [RoomType.FREESTANDING_FIREPLACE]: true,
              [RoomType.LANDSCAPE]: true,
            },
            borderPosition: BorderPosition.FRAMING_OUTSIDE,
          },
        ],
      },
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.GARAGE_DETACH]: true,
        },
        includeAnyInSameRoomSets: true,
        otherRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
        exteriorRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
      },
    ],
    exclude: [],
  },
  [FloorAreaTakeoffType.TOTAL_GARAGE_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.GARAGE]: true,
        },
        includeAnyInSameRoomSets: true,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
        specificRoomBorderPositions: [
          {
            type: AreaRuleType.BY_ROOM_TYPE,
            roomTypes: {
              [RoomType.PATIO]: true,
              [RoomType.FRONT_PORCH]: true,
              [RoomType.REAR_PORCH]: true,
              [RoomType.WALK]: true,
              [RoomType.BREEZEWAY]: true,
              [RoomType.FREESTANDING_FIREPLACE]: true,
              [RoomType.LANDSCAPE]: true,
            },
            borderPosition: BorderPosition.FRAMING_OUTSIDE,
          },
        ],
      },
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.GARAGE_DETACH]: true,
        },
        includeAnyInSameRoomSets: true,
        otherRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
        exteriorRoomBorderPosition: BorderPosition.FRAMING_OUTSIDE,
      },
    ],
    exclude: [],
  },
  [FloorAreaTakeoffType.FRONT_PORCH_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.FRONT_PORCH]: true,
          [RoomType.PORTICO]: true,
        },
        includeAnyInSameRoomSets: false,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.CENTERLINE,
      },
    ],
    exclude: [],
    includeSupplementalAreas: { [SupplementalArea.FRONT_PORCH_COLONNADE]: true },
  },
  [FloorAreaTakeoffType.REAR_PORCH_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.REAR_PORCH]: true,
        },
        includeAnyInSameRoomSets: false,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.CENTERLINE,
      },
    ],
    exclude: [],
    includeSupplementalAreas: { [SupplementalArea.REAR_PORCH_COLONNADE]: true },
  },
  [FloorAreaTakeoffType.COVERED_PATIO_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.BREEZEWAY]: true,
        },
        includeAnyInSameRoomSets: false,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.CENTERLINE,
      },
    ],
    exclude: [],
  },
  [FloorAreaTakeoffType.UNCOVERED_PATIO_AREA]: {
    include: [
      {
        type: AreaRuleType.BY_ROOM_TYPE,
        roomTypes: {
          [RoomType.WALK]: true,
          [RoomType.PATIO]: true,
        },
        includeAnyInSameRoomSets: false,
        otherRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
        exteriorRoomBorderPosition: BorderPosition.FRAMING_INSIDE,
      },
    ],
    exclude: [],
  },
};
